import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { fetchTemplateData, sendEmailsAPI, scheduleEmailAPI, insertSubscribersAPI } from '../services/api';

// sanitize name during insert subscribers
const sanitizeText = (text) => {
    return text.replace(
      /([\uD800-\uDBFF][\uDC00-\uDFFF])/g, '' // Remove 4-byte UTF-16 surrogate pairs (e.g., emojis)
    );
};

// Fetch template data from the backend API
export const getTemplateData = createAsyncThunk('email/getTemplateData', async (params) => {
    const response = await fetchTemplateData(params);
    return response.data;
});

// Send emails with the selected template
export const sendEmails = createAsyncThunk(
    'email/sendEmails',
    async ({ keys, subject, campaign_name, html_body, batch_count }, { rejectWithValue }) => {
        try {
            const response = await sendEmailsAPI({
                keys,
                subject,
                campaign_name,
                html_body,
                batch_count,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Schedule an email
export const scheduleEmail = createAsyncThunk(
    'email/scheduleEmail',
    async (emailData, { rejectWithValue }) => {
        try {
            const response = await scheduleEmailAPI(emailData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Action to reset recipients
export const resetRecipients = () => ({
    type: 'email/resetRecipients',
});

// Action to add a recipient
export const addRecipient = (email) => (dispatch, getState) => {
    const { recipients } = getState().email;

    // Check if the email already exists in the recipient list
    if (!recipients.includes(email)) {
        dispatch({
            type: 'email/addRecipientSuccess',
            payload: email,
        });
    } else {
        // Optionally, you can show a message if the email is a duplicate
        toast.info(`${email} is already in the recipient list.`);
    }
};

// Upload Subscribers using API (Batch Upload)
export const uploadSubscribers = createAsyncThunk(
    'email/uploadSubscribers',
    async (data, { rejectWithValue }) => {
        const batchSize = 1000;
        const totalBatches = Math.ceil(data.length / batchSize);
        let insertedCount = 0;

        for (let i = 0; i < totalBatches; i++) {
            const batch = data.slice(i * batchSize, (i + 1) * batchSize).map(subscriber => ({
                name: sanitizeText(subscriber.name?.trim() || '') || '',  // Ensure empty name is sent as an empty string
                email: subscriber.email,
                status: subscriber.status || 'subscribed'  // Default status if missing
            }));

            try {
                const response = await insertSubscribersAPI(batch);
                insertedCount += response.inserted_count;
            } catch (error) {
                console.error('Error uploading batch:', error);
                return rejectWithValue(error.response?.data || 'Failed to insert subscribers');
            }
        }
        toast.success(`Upload complete! ${insertedCount} records inserted.`);
        return { insertedCount };
    }
);

export const emailSlice = createSlice({
    name: 'email',
    initialState: {
        templates: [],
        recipients: [],
        isLoading: false,
        successEmails: [],
        failedEmails: [],
        scheduledEmails: [],
    },
    extraReducers: (builder) => {
        builder
            .addCase(getTemplateData.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getTemplateData.fulfilled, (state, action) => {
                state.templates = action.payload;
                state.isLoading = false;
            })
            .addCase(getTemplateData.rejected, (state) => {
                state.isLoading = false;
            })

            // Send emails
            .addCase(sendEmails.pending, (state) => {
                state.isLoading = true;
                state.successEmails = [];
                state.failedEmails = [];
            })
            .addCase(sendEmails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successEmails = action.payload.successful_emails;
                state.failedEmails = action.payload.failed_emails;
            })
            .addCase(sendEmails.rejected, (state, action) => {
                state.isLoading = false;
                state.failedEmails = action.payload.failed_emails;
            })

            // Schedule email
            .addCase(scheduleEmail.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(scheduleEmail.fulfilled, (state, action) => {
                state.isLoading = false;
                state.scheduledEmails.push(action.payload);
                toast.success('Email scheduled successfully!');
            })
            .addCase(scheduleEmail.rejected, (state, action) => {
                state.isLoading = false;
                toast.error('Failed to schedule email');
            })

            // Upload Subscribers
            .addCase(uploadSubscribers.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(uploadSubscribers.fulfilled, (state, action) => {
                state.isLoading = false;
                toast.success(`Upload complete! ${action.payload.insertedCount} records inserted.`);
            })
            .addCase(uploadSubscribers.rejected, (state, action) => {
                state.isLoading = false;
                const errorMessage = action.payload?.message || 'Failed to upload subscribers.';
                console.error('Upload Error:', errorMessage);
                toast.error(errorMessage);
            });
    },
});

export const { removeRecipient } = emailSlice.actions;

export default emailSlice.reducer;